import { useCallback, useContext, useEffect, useState } from 'react';

import { Button, Controller } from '@selina-finance/ui';
import { chain, get, isEmpty } from 'lodash';

import { ApplicationContext } from '@App/contexts/Application';
import { BUILDING_IDENTIFIER_FIELDS } from '@Shared/constants';
import {
    isMoreThanThreeYears,
    isMoreThanThreeYearsFromApplication,
} from '@Shared/helpers/dateDifference';
import { AddressSection } from '@Views/eligibility/addressRepeaterView/views/AddressSection/AdressSection';
import { InputSection } from '@Views/eligibility/addressRepeaterView/views/InputSection/InputSection';
import { getMatchedAddress } from '@src/shared/helpers/address';

import { AddressErrorInfoBox } from './AddressErrorInfoBox/AddressErrorInfoBox';

interface IAddressViewProps {
    index: number;
    addressFieldName: string;
    formItems: any;
    formikProps: any;
    isPrimaryApplicant: boolean;
    arrayHelpers: any;
    hasAddressItemAddedRef: any;
}

export const AddressRepeaterViewContent = ({
    index,
    addressFieldName,
    formItems,
    formikProps,
    arrayHelpers,
    isPrimaryApplicant,
    hasAddressItemAddedRef,
}: IAddressViewProps) => {
    const { fetchAddressesByPostcode, isAddressLookupLoading } =
        useContext(ApplicationContext);
    const [isAddressSectionEdit, setIsAddressSectionEdit] = useState(false);
    const [isInputSectionEdit, setIsInputSectionEdit] = useState(false);
    const [notTouched, setNotTouched] = useState(false);
    const addressItemName = `${addressFieldName}[${index}]`;
    const addressPath = `${addressItemName}.address`;
    const addressIsSavedPath = `${addressPath}.isSaved`;
    const fromDate = formikProps.values.addresses[index]?.livedIn.from;
    const toDate = formikProps.values.addresses[index]?.livedIn.to;

    const isInEditMode = isAddressSectionEdit || isInputSectionEdit;
    const saveAddressError = get(formikProps.errors, addressIsSavedPath);
    const shouldShowSaveAddressError =
        typeof saveAddressError === 'string' &&
        isInEditMode &&
        formikProps.getFieldMeta(addressIsSavedPath).touched;
    const addressErrors = get(formikProps.errors, addressPath, {});
    const hasBuildingIdentifierError = BUILDING_IDENTIFIER_FIELDS.some(
        (field) => Boolean(addressErrors?.[field]),
    );
    const shouldShowBuildingIdentifierError =
        isInEditMode && hasBuildingIdentifierError;

    const isMoreThanThree = isMoreThanThreeYears(formikProps.values.addresses);
    const isMoreThanThreeFromApplication = isMoreThanThreeYearsFromApplication(
        formikProps.values.addresses,
    );

    const resetAddressIsSavedState = useCallback(() => {
        formikProps.setFieldTouched(addressIsSavedPath, false, false);
        formikProps.setFieldError(addressIsSavedPath, undefined);
        formikProps.setFieldValue(addressIsSavedPath, false, false);
    }, [addressIsSavedPath, formikProps]);

    const enterEditMode = ({
        isAddressEdit,
        isInputEdit,
    }: {
        isAddressEdit: boolean;
        isInputEdit: boolean;
    }) => {
        resetAddressIsSavedState();
        setIsAddressSectionEdit(isAddressEdit);
        setIsInputSectionEdit(isInputEdit);
    };

    useEffect(() => {
        if (index === 0 && fromDate === '' && !isInputSectionEdit) {
            resetAddressIsSavedState();
            setIsAddressSectionEdit(false);
            setIsInputSectionEdit(true);
        }
    }, [fromDate, index, isInputSectionEdit, resetAddressIsSavedState]);

    useEffect(() => {
        if (
            index !== 0 &&
            isEmpty(formikProps.values.addresses[index].address) &&
            (!isAddressSectionEdit || !isInputSectionEdit)
        ) {
            setIsAddressSectionEdit(true);
            setIsInputSectionEdit(true);
        }

        if (
            index !== 0 &&
            (fromDate === '' || toDate === '') &&
            (!isAddressSectionEdit || !isInputSectionEdit)
        ) {
            resetAddressIsSavedState();
            setIsAddressSectionEdit(true);
            setIsInputSectionEdit(true);
        }

        if (
            index === 0 &&
            isMoreThanThreeFromApplication === false &&
            isMoreThanThree === false &&
            !hasAddressItemAddedRef.current
        ) {
            arrayHelpers.push({
                address: {},
                livedIn: {},
            });

            hasAddressItemAddedRef.current = true;
        }
    }, [
        addressIsSavedPath,
        fromDate,
        toDate,
        index,
        formikProps,
        formikProps.values.addresses,
        isAddressSectionEdit,
        isInputSectionEdit,
        isMoreThanThreeFromApplication,
        arrayHelpers,
        isMoreThanThree,
        hasAddressItemAddedRef,
        resetAddressIsSavedState,
    ]);

    const onSaveValidateFields = [
        `${addressItemName}.livedIn.from`,
        `${addressItemName}.livedIn.to`,
        `${addressPath}.postcode`,
        addressPath,
    ];

    const resolveAddressBeforeSave = async () => {
        const currentAddress = get(formikProps.values, addressPath);

        if (!currentAddress?.postcode) {
            return currentAddress;
        }

        const addresses = await fetchAddressesByPostcode(
            currentAddress.postcode,
            addressPath,
        );

        return getMatchedAddress(addresses, currentAddress);
    };

    const onSave = async () => {
        const resolvedAddress = await resolveAddressBeforeSave();
        const nextValues = {
            ...formikProps.values,
            addresses: formikProps.values.addresses.map(
                (addressItem: any, currentIndex: number) =>
                    currentIndex === index
                        ? {
                              ...addressItem,
                              address: {
                                  ...(resolvedAddress || {}),
                                  isSaved: true,
                              },
                          }
                        : addressItem,
            ),
        };

        formikProps.setValues(nextValues, false);
        onSaveValidateFields.forEach((field) => {
            formikProps.setFieldTouched(field, true, false);
        });

        const validationErrors = await formikProps.validateForm(nextValues);

        const error = chain(onSaveValidateFields)
            .map((field) => get(validationErrors, field))
            .compact()
            .value();

        setNotTouched(false);

        if (error.length === 0) {
            setNotTouched(true);
            formikProps.setFieldTouched(addressIsSavedPath, false, false);
            formikProps.setFieldError(addressIsSavedPath, undefined);
            setIsAddressSectionEdit(false);
            setIsInputSectionEdit(false);

            !isMoreThanThree &&
                arrayHelpers.push({
                    address: {},
                    livedIn: {},
                });

            removeExtraAddresses();
            return;
        }

        formikProps.setFieldValue(addressIsSavedPath, false, false);
    };

    const removeExtraAddresses = () => {
        const allElementsBeforeCurrent = formikProps.values.addresses.filter(
            (_: any, currentIndex: number) => {
                return currentIndex <= index;
            },
        );

        if (isMoreThanThreeYears(allElementsBeforeCurrent)) {
            for (let i = formikProps.values.addresses.length - 1; i >= 0; i--) {
                if (i > index) {
                    arrayHelpers.remove(i);
                }
            }
        }
    };

    return (
        <Controller
            label={index === 0 ? 'Current address' : 'Previous address'}
        >
            <AddressSection
                index={index}
                notTouched={notTouched}
                isEdit={isAddressSectionEdit}
                addressFieldName={addressFieldName}
                formItems={formItems}
                formikProps={formikProps}
                onEditButtonClick={() =>
                    enterEditMode({
                        isAddressEdit: true,
                        isInputEdit: true,
                    })
                }
            />
            <InputSection
                index={index}
                isEdit={isInputSectionEdit}
                notTouched={notTouched}
                addressFieldName={addressFieldName}
                formItems={formItems}
                formikProps={formikProps}
                isPrimaryApplicant={isPrimaryApplicant}
            />

            {isInputSectionEdit && (
                <div className='pt-6'>
                    <Button
                        type='button'
                        size='large'
                        color='secondary-flat'
                        className='w-full'
                        onClick={onSave}
                        loading={Boolean(isAddressLookupLoading[addressPath])}
                    >
                        Save address
                    </Button>

                    <AddressErrorInfoBox
                        saveAddressError={saveAddressError}
                        shouldShowBuildingIdentifierError={
                            shouldShowBuildingIdentifierError
                        }
                        shouldShowSaveAddressError={shouldShowSaveAddressError}
                    />
                </div>
            )}
        </Controller>
    );
};
